import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import AdminPage from './pages/AdminPage.jsx';
import Topbar from './components/Topbar.js';
import Home from './pages/Home.js';
import Cockpit from './pages/Cockpit.js';
import LoginForm from './components/LoginForm.js';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.js';
import useAnalytics from './hooks/useAnalytics.js'; // Import du hook de tracking

// Wrapper component to handle cockpit context
const AppContent = () => {
  const { isAuthenticated, currentUser, logout, fetchWithAuth } = useAuth();
  const [cockpitTabs, setCockpitTabs] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [cockpitSlug, setCockpitSlug] = useState(null);
  const location = useLocation();

  // Utilisation du hook pour le tracking de base des changements de route
  useAnalytics({ currentUser: isAuthenticated ? currentUser : null });

  // Check if we're on a /c/:slug route
  const isCockpitRoute = location.pathname.startsWith('/c/');

  // Reset cockpit context when navigating away from cockpits
  useEffect(() => {
    if (!location.pathname.startsWith('/cockpits/')) {
      setCockpitTabs(null);
      setActiveTab(0);
      setCockpitSlug(null);
    }
  }, [location.pathname]);

  // Identité stable exigée : Cockpit.js l'a dans les dépendances de son effet de
  // chargement ET l'appelle depuis cet effet. Recréée à chaque rendu, chaque succès
  // déclenchait re-rendu → nouvelle identité → re-fetch : boucle infinie qui
  // détruisait/recréait les iframes en continu (mesuré ~24 req/s en prod).
  const setCockpitContext = useCallback((slug, tabs, initialTab = 0) => {
    setCockpitSlug(slug);
    setCockpitTabs(tabs);
    setActiveTab(initialTab);
  }, []);

  // Fonction handleTabChange mise à jour pour gérer les slugs
  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);

    // Mettre à jour l'URL si des tabs sont disponibles
    if (cockpitTabs && cockpitTabs[tabIndex] && cockpitTabs[tabIndex].slug) {
      window.history.pushState(null, '', `#${cockpitTabs[tabIndex].slug}`);
    }
  };

  // Vérifier si un slug est présent dans l'URL lors du chargement
  useEffect(() => {
    if (cockpitTabs && cockpitTabs.length > 0 && location.hash) {
      const tabSlug = location.hash.replace('#', '');
      const tabIndex = cockpitTabs.findIndex(tab => tab.slug === tabSlug);
      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
      }
    }
  }, [cockpitTabs, location.hash]);

  // If we're on the /c/:slug route, display the cockpit even if not authenticated
  if (isCockpitRoute) {
    return (
      <div className="App min-h-screen bg-gray-50 transition-colors duration-200">
        <main className='w-full p-0 m-0'>
          {/* Cockpit public : la config vient de la base, une valeur invalide ne doit pas
              rendre un écran blanc à un utilisateur qui n'a même pas de session. */}
          <ErrorBoundary key={location.pathname} title="Ce cockpit n'a pas pu s'afficher">
            <Routes>
              <Route
                path="/c/:slug"
                element={
                  <Cockpit
                    fetchWithAuth={fetchWithAuth}
                    fullscreen={true}
                    currentUser={currentUser}
                    logout={logout}
                  />
                }
              />
            </Routes>
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-gray-100  dark:bg-gray-900 transition-colors duration-200">
      {/* Only show Topbar if not on a /r/:slug route */}
      <Topbar
        currentUser={isAuthenticated ? currentUser : null}
        logout={isAuthenticated ? logout : null}
        cockpitTabs={cockpitTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {!isAuthenticated ? (
        <LoginForm />
      ) : (
        <main className='w-full p-0 m-0'>
          {/* `key` sur le chemin : sans lui l'état d'erreur survit à la navigation et une
              section cassée transforme l'application entière en cul-de-sac. */}
          <ErrorBoundary key={location.pathname}>
            <Routes>
              <Route path="/" element={
                <Home
                  currentUser={currentUser}
                  fetchWithAuth={fetchWithAuth}
                />
              } />
              <Route
                path="/admin/*"
                element={
                  <AdminPage
                    fetchWithAuth={fetchWithAuth}
                    currentUser={currentUser}
                  />
                }
              />
              <Route
                path="/cockpits/:slug"
                element={
                  <Cockpit
                    fetchWithAuth={fetchWithAuth}
                    setCockpitContext={setCockpitContext}
                    activeTab={activeTab}
                    currentUser={currentUser}
                    logout={logout}
                  />
                }
              />
            </Routes>
          </ErrorBoundary>
        </main>
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
